import { useMemo } from 'react';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';
import ClockIcon from '@atlaskit/icon/core/clock';
import { IconTile } from '@atlaskit/icon';
import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/en'; // locale-data for en

import IconWithContent from '../IconWithContent';
import { useInfo } from '../../../Flow/ConsentContextProvider';

export const AppAge = () => {
  const { client } = useInfo();
  const messageValues = useMemo(
    () => getTimeMessageProperties(client?.createdAt),
    [client?.createdAt],
  );
  if (!messageValues) {
    return <></>;
  }
  return (
    <IconWithContent
      icon={
        <IconTile icon={ClockIcon} label={''} appearance="gray" size="medium" />
      }
      text={
        messageValues.value === 0 && messageValues.unit === 'day' ? (
          <FormattedMessage
            id="trello.appAge.createdToday"
            defaultMessage="Created today"
            description="Notifies the user that this app had only been created today"
          />
        ) : (
          <FormattedMessage
            id="trello.appAge"
            defaultMessage="Created {time}"
            description="A trust indicator which conveys to the user how long this app has existed. The time parameter will be something along the lines of '2 years ago' or '3 months ago'"
            values={{
              time: <FormattedRelativeTime {...messageValues} />,
            }}
          />
        )
      }
    />
  );
};

type TimeProp = {
  value: number;
  unit: 'day' | 'month' | 'year';
};

export function getTimeMessageProperties(
  createdAt: number | null | undefined,
): TimeProp | null {
  if (createdAt == null) {
    console.warn('No creation timestamp provided, skipping...');
    return null;
  }
  const currentTime = Date.now();
  const secondDiff = Math.floor((currentTime - createdAt) / 1_000);
  if (secondDiff <= 0) {
    console.warn('Created timestamp is after current time');
    return null;
  }
  const dayDiff = Math.floor(secondDiff / (3_600 * 24));
  if (dayDiff <= 28) {
    return {
      value: -dayDiff,
      unit: 'day',
    };
  }
  const monthDiff = Math.floor(dayDiff / 28);
  if (dayDiff < 365) {
    return {
      value: -monthDiff,
      unit: 'month',
    };
  }
  const yearDiff = Math.floor(dayDiff / 365);
  return {
    value: -yearDiff,
    unit: 'year',
  };
}
